"use client"

import { useEffect, useRef } from "react"

const SCRAMBLE_CHARS = "▓▒░█▄▀▌▐■▪◆◇◊‹›⌐¬¦¡¿●○◐◑◒◓✦✧"

/**
 * Imperative scramble on a ref — avoids React state updates every ~32ms (main-thread / TBT win).
 */
export function useScramble(target: string, intervalMs = 4200, scrambleMs = 800) {
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      el.textContent = target
      return
    }

    let alive = true
    let betweenId: ReturnType<typeof setTimeout>
    let idleRaf = 0

    const clearBetween = () => clearTimeout(betweenId)

    const cycle = () => {
      if (!alive) return
      const start = performance.now()
      const step = () => {
        if (!alive) return
        const t = Math.min(1, (performance.now() - start) / scrambleMs)
        const reveal = Math.floor(t * target.length)
        const out = target
          .split("")
          .map((ch, i) => {
            if (ch === " " || ch === "_" || i < reveal) return ch
            return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]!
          })
          .join("")
        el.textContent = out
        if (t < 1) idleRaf = requestAnimationFrame(step)
        else betweenId = setTimeout(cycle, intervalMs)
      }
      idleRaf = requestAnimationFrame(step)
    }

    betweenId = setTimeout(cycle, 600)

    return () => {
      alive = false
      clearBetween()
      cancelAnimationFrame(idleRaf)
      el.textContent = target
    }
  }, [target, intervalMs, scrambleMs])

  return ref
}
